import { assertNeverError } from "@p0security/shared/types/index";

import {
  existingIamWriteAndAssessmentItems,
  existingIamWriteItems,
} from "./install-options.ts";
import type {
  AwsFunctionCaller,
  AwsIntegration,
  AwsPolicyUse,
} from "./types.ts";
import { type AwsPartition, isAwsPartition } from "./util.ts";

const awsPartitionFromRdsSecurityPerimeter = (
  config: AwsIntegration,
  accountId: string
) => {
  const items = existingIamWriteItems(config);
  return items.find(([id, _item]) => id === accountId)?.[1]?.awsPartition?.type;
};

const awsPartitionFromInventory = (
  config: AwsIntegration,
  accountId: string
) => {
  const items = existingIamWriteAndAssessmentItems(config);
  return items.find(([id, _item]) => id === accountId)?.[1]?.awsPartition?.type;
};

const awsPartitionFromAccessLogs = (
  config: AwsIntegration,
  accountId: string
) => {
  const items = existingIamWriteAndAssessmentItems(config);
  return items.find(([id, _item]) => id === accountId)?.[1]?.awsPartition?.type;
};

const awsPartitionFromFunctionCaller = (config: AwsFunctionCaller) => {
  const { awsPartition } = config;
  return isAwsPartition(awsPartition) ? awsPartition : undefined;
};

const unsafeAwsPartitionFromUse = (
  config: AwsIntegration,
  installId: string,
  use: AwsPolicyUse
): AwsPartition | undefined => {
  switch (use) {
    case "iam-write":
    case "iam-assessment":
      return config[use]?.[installId]?.awsPartition?.type;
    case "function-caller": {
      const fc = config[use]?.[installId];
      return fc ? awsPartitionFromFunctionCaller(fc) : undefined;
    }
    case "access-logs":
      return awsPartitionFromAccessLogs(config, installId);
    case "rds-security-perimeter": {
      const rds = config[use]?.[installId];
      return rds
        ? awsPartitionFromRdsSecurityPerimeter(config, rds.accountId)
        : undefined;
    }
    case "inventory":
      return awsPartitionFromInventory(config, installId);
    default:
      throw assertNeverError(use);
  }
};

export const awsPartitionFromUse = (
  config: AwsIntegration,
  installId: string,
  use: AwsPolicyUse
): AwsPartition => {
  const awsPartition = unsafeAwsPartitionFromUse(config, installId, use);
  // Fall back to "aws" partition if we cannot determine it. This is extra safety
  // because all installs should have an awsPartition defined. This file is in `shared`,
  // must use console.log instead of logger.
  if (!awsPartition) {
    // eslint-disable-next-line no-console
    console.error(
      `AWS partition not found for AWS account ${installId} installed for ${use}`
    );
    return "aws";
  }
  return awsPartition;
};
